import {
    Badge,
    BlockStack,
    Box,
    Card,
    Icon,
    InlineStack,
    Link,
    Typography,
    type BadgeTone,
} from '@pv-frontend/ui-components-web';

/**
 * Plain view model for the ops "all-green" board — deliberately decoupled from
 * any Relay generated type so this presentational component is unit-testable
 * without a backend or the Babel transform. The screen maps its data source
 * (today a static manifest; tomorrow the platform status API) into these rows.
 */
export type ServiceHealth = 'healthy' | 'degraded' | 'down' | 'unknown';
export type DeployIntegrity = 'verified' | 'drift' | 'unknown';

export interface OpsServiceView {
    name: string;
    /** Liveness/health-probe verdict. */
    health: ServiceHealth;
    /** Deploy-integrity: does the running build match the released build? */
    integrity: DeployIntegrity;
    /** Running version, e.g. `1.4.2` (or `—` when not yet known). */
    version: string;
    /** Running commit — short SHA (or `—` when not yet known). */
    commit: string;
    /** Set when `integrity === 'drift'`: what the live WebFetch probe saw vs the expected release. */
    drift?: string;
    /** Azure Log Analytics deep-link. */
    logsUrl: string;
    /** Azure Container Apps metrics deep-link. */
    metricsUrl: string;
}

export interface OpsVerdict {
    /** Every service is healthy *and* deploy-integrity verified. */
    allGreen: boolean;
    /** Count of services that are degraded, down, or drifting. */
    attention: number;
    /** Any service still lacks a live signal (health or integrity unknown). */
    pending: boolean;
}

/** Roll the per-service rows up into the single top-level verdict. */
export function summariseOps(services: readonly OpsServiceView[]): OpsVerdict {
    const attention = services.filter(
        (s) => s.health === 'degraded' || s.health === 'down' || s.integrity === 'drift',
    ).length;
    const pending = services.some((s) => s.health === 'unknown' || s.integrity === 'unknown');
    const allGreen =
        services.length > 0 &&
        services.every((s) => s.health === 'healthy' && s.integrity === 'verified');
    return { allGreen, attention, pending };
}

const HEALTH: Record<ServiceHealth, { label: string; tone: BadgeTone }> = {
    healthy: { label: 'Healthy', tone: 'success' },
    degraded: { label: 'Degraded', tone: 'warning' },
    down: { label: 'Down', tone: 'critical' },
    unknown: { label: 'Unknown', tone: 'neutral' },
};

const INTEGRITY: Record<DeployIntegrity, { label: string; tone: BadgeTone }> = {
    verified: { label: 'Verified', tone: 'success' },
    drift: { label: 'Drift', tone: 'critical' },
    unknown: { label: 'Unknown', tone: 'neutral' },
};

/** The glanceable top-level banner: all-green, needs-attention, or awaiting signals. */
function VerdictBanner({ verdict }: { verdict: OpsVerdict }) {
    const { background, borderColor, iconName, iconTone, title, detail } = verdictAppearance(verdict);
    return (
        <Box
            background={background}
            borderColor={borderColor}
            borderWidth="0165"
            borderRadius="300"
            padding="500"
        >
            <InlineStack gap="400" blockAlign="center" wrap={false}>
                <Icon name={iconName} size={32} tone={iconTone} />
                <BlockStack gap="050">
                    <Typography variant="heading-md" as="h2" fontWeight="semibold">
                        {title}
                    </Typography>
                    <Typography variant="body-sm" tone="text-secondary">
                        {detail}
                    </Typography>
                </BlockStack>
            </InlineStack>
        </Box>
    );
}

function verdictAppearance(verdict: OpsVerdict) {
    if (verdict.attention > 0) {
        const n = verdict.attention;
        return {
            background: 'critical-subdued',
            borderColor: 'critical',
            iconName: 'alert-triangle',
            iconTone: 'text-critical',
            title: "Something's wrong",
            detail: `${n} service${n === 1 ? '' : 's'} need${n === 1 ? 's' : ''} attention.`,
        } as const;
    }
    if (verdict.allGreen) {
        return {
            background: 'success-subdued',
            borderColor: 'success',
            iconName: 'check-circle',
            iconTone: 'text-success',
            title: 'All systems green',
            detail: 'Every service is healthy and running its released build.',
        } as const;
    }
    return {
        background: 'bg-low-transparency',
        borderColor: 'border-gray',
        iconName: 'alert-triangle',
        iconTone: 'text-secondary',
        title: 'Awaiting live signals',
        detail: 'Health and deploy-integrity checks have not reported yet.',
    } as const;
}

function ServiceRow({ service }: { service: OpsServiceView }) {
    const health = HEALTH[service.health];
    const integrity = INTEGRITY[service.integrity];
    return (
        <Box
            paddingInline="400"
            paddingBlock="400"
            borderColor="border-card"
            borderBlockStartWidth="0165"
        >
            <InlineStack align="space-between" blockAlign="start" gap="400">
                <BlockStack gap="150">
                    <InlineStack gap="200" blockAlign="center" wrap>
                        <Typography variant="body-md" fontWeight="semibold">
                            {service.name}
                        </Typography>
                        <Badge tone={health.tone} dot>
                            {health.label}
                        </Badge>
                        <Badge tone={integrity.tone} dot>
                            {`Deploy: ${integrity.label}`}
                        </Badge>
                    </InlineStack>
                    <Typography variant="body-sm" tone="text-secondary">
                        {`v${service.version} · ${service.commit}`}
                    </Typography>
                    {service.integrity === 'drift' && service.drift ? (
                        <Typography variant="body-sm" tone="text-warning">
                            {`Drift: ${service.drift}`}
                        </Typography>
                    ) : null}
                </BlockStack>
                <InlineStack gap="300" blockAlign="center" wrap>
                    <Link href={service.logsUrl} external>
                        Logs
                    </Link>
                    <Link href={service.metricsUrl} external>
                        Metrics
                    </Link>
                </InlineStack>
            </InlineStack>
        </Box>
    );
}

/** The full board: top-level verdict banner over a per-service status table. */
export function OpsBoard({ services }: { services: readonly OpsServiceView[] }) {
    const verdict = summariseOps(services);
    return (
        <BlockStack gap="500">
            <VerdictBanner verdict={verdict} />
            {services.length === 0 ? (
                <Card>
                    <Typography tone="text-secondary">No services are configured yet.</Typography>
                </Card>
            ) : (
                <Card padding="0">
                    <Box paddingInline="400" paddingBlock="300">
                        <Typography variant="body-sm" fontWeight="medium" tone="text-secondary">
                            Services
                        </Typography>
                    </Box>
                    {services.map((service) => (
                        <ServiceRow key={service.name} service={service} />
                    ))}
                </Card>
            )}
        </BlockStack>
    );
}
